import type { RunCheck } from './case-script';
import type { WebviewSubmission } from './webview-submission';
import type { RunJudgeEntry } from './validate-judges';
import {
  RESPONSE_TARGET_S,
  passes,
  qualityScore,
  verdictCriteria,
  type CriterionKey,
  type RubricVerdict,
  type VerdictCriterion,
} from '../../utils/evaluation-rubric';

/**
 * Validate's runs: one per conversation — a Test chat, or a simulated
 * scenario. Each bot reply in it is scored on its own (`turnScores`), and the
 * conversation as a whole gets its own score (`score`), which is what passes
 * or fails the run.
 *
 * The testing tool keeps these in Postgres behind its own SSO. Pro Studio has
 * no store for them yet, so they live in this browser, per solution
 * (`loadRuns`/`saveRuns`). That is why there is no "All runs" view: a run
 * only exists for the person who ran it, on the machine they ran it on.
 */

/** The last thing the tester said in a chat — how its run is titled. */
export const latestUserText = (
  transcript: readonly { role: string; text?: string }[]
): string =>
  [...transcript].reverse().find((t) => t.role === 'user' && t.text?.trim())
    ?.text ?? '';

export interface ValidateOption {
  label: string;
  value: string;
}

export interface ValidateTurn {
  role: 'user' | 'bot';
  text: string;
  options?: ValidateOption[];
  /** On a bot reply: ms from the message leaving to the reply's first frame. */
  latencyMs?: number;
  /**
   * On a customer message: when it was sent (epoch ms). Lines the bot
   * engine logged are matched to the reply they belong to by this.
   */
  at?: number;
  /**
   * Seeded context: the earlier part of a conversation a scenario was started
   * partway into. Replayed to the real bot, but not scored or timed.
   */
  seeded?: boolean;
  /**
   * The platform's own message rather than the bot's — "This conversation has
   * ended." when the flow ends the chat. Shown, never scored as a reply.
   */
  system?: boolean;
  /**
   * Written by a live agent after the chat was handed off. Always `system`
   * too, so nothing scores it as the bot's reply; this only names the writer.
   */
  agent?: boolean;
  /**
   * On the customer's turn that closed a webview: what the webview handed
   * over before it closed, sensitive values masked (`webview-submission.ts`).
   */
  submitted?: WebviewSubmission;
  /**
   * On a bot reply: a webview it opened — a form or page the customer fills
   * in, which answers the bot itself (`webviewOf` in `live-chat.ts`).
   */
  webview?: ValidateWebview;
  /**
   * On a bot reply: the embed types its frames carried (a slider, a date
   * picker …) that this page cannot render; `embedsOf` in `embeds.ts`.
   */
  embeds?: string[];
}

export interface ValidateWebview {
  url: string;
  /** What the bot called it, when it named it. */
  label?: string;
  /** What the customer "says" when it closes ("Time selected"). */
  closeMessage?: string;
}

/** Where a scenario picks the conversation up. */
export type StartPosition = 'beginning' | 'mid' | 'end';

export const START_POSITIONS: {
  value: StartPosition;
  label: string;
  hint: string;
}[] = [
  {
    value: 'beginning',
    label: 'Beginning',
    hint: 'The customer opens the conversation.',
  },
  {
    value: 'mid',
    label: 'Mid',
    hint: 'Picks up partway through, after a couple of earlier messages.',
  },
  {
    value: 'end',
    label: 'End',
    hint: 'Picks up near the end, as the customer is wrapping up.',
  },
];

export type RunKind = 'test' | 'scenario';

export type RunScore =
  /** Not asked for yet — a Test chat's conversation score until it ends. */
  | { status: 'idle' }
  | { status: 'pending' }
  | {
      status: 'scored';
      verdict: RubricVerdict;
      summary: string;
      reasons: Partial<Record<CriterionKey, string>>;
      /** What the bot got wrong — shown only when the run fails. */
      whyFailed?: string | null;
      /** How the tone landed, when tone fell short. */
      toneNote?: string | null;
    }
  | { status: 'error'; message: string };

export interface RunChannel {
  appId: string;
  name: string;
  env: 'live' | 'sandbox';
  /**
   * Set on a VOICE channel tested in the browser. The Engagement API opens no
   * browser session on a voice channel (404), so the conversation runs through
   * `appId` — the solution's widget channel in the same environment, same bot —
   * with the microphone and the browser's voice standing in for the phone.
   */
  voiceChannelId?: string;
}

/** A channel's identity in a picker: voice and its widget share an `appId`. */
export const channelKey = (channel: RunChannel): string =>
  channel.voiceChannelId ? `voice:${channel.voiceChannelId}` : channel.appId;

/** An agent the designer expects to handle a run — a Build agent group. */
export interface ExpectedAgent {
  /** The group row's id when the run was recorded. */
  id: string;
  /** Its name then, so the run still reads right if the group is renamed. */
  title: string;
}

export interface ValidateRun {
  id: string;
  kind: RunKind;
  /** A Test run's customer message, or a scenario's description. */
  title: string;
  /** What the scenario should do, when the designer said. */
  expectation?: string;
  /**
   * Which agent should handle it, when the designer said. The run's detail
   * checks it against the agents the chat's engine trace walked through
   * (`routingVerdict`), where the trace can be read.
   */
  expectedAgent?: ExpectedAgent | null;
  channel: RunChannel;
  /** What was running on the channel, e.g. "v3" — the published version. */
  versionLabel: string | null;
  chatId: string;
  /** The run's conversation. A Test run includes the turns before it, for context. */
  transcript: ValidateTurn[];
  /**
   * The reply a run was about, as an index into `transcript`, for a run
   * recorded when each Test reply was its own run. Every run now covers its
   * whole conversation (null); old ones are merged on load
   * (`mergeTestRunsByChat`), so this is only non-null for a lone old reply.
   */
  focusIndex: number | null;
  startedAt: number;
  endedAt: number;
  /** The whole conversation's score — what passes or fails the run. */
  score: RunScore;
  /**
   * Each scored bot reply, keyed by its index in `transcript` (a string, as
   * JSON keys are). See `scorableTurnIndices` for which replies get one.
   */
  turnScores?: Record<string, RunScore>;
  /** Why a scenario's conversation stopped before the customer was done. */
  stoppedBecause?: string | null;
  /** Where a scenario picked the conversation up; unset means the beginning. */
  startAt?: StartPosition;
  /**
   * What the solution's other judges made of it (guardrails, topics…),
   * keyed by judge id. The rubric's verdict is `score`.
   */
  judgeResults?: Record<string, RunJudgeEntry>;
  /**
   * Script `expect:` steps and the case's must-say / must-not-say patterns.
   * Any that failed fail the run, whatever the score.
   */
  checks?: RunCheck[];
  /** The AI Gateway model the solution's AI turns ran on, when overridden. */
  model?: string;
  /** Set on a run a suite produced: which suite, which run of it, which case. */
  suite?: {
    suiteId: string;
    suiteRunId: string;
    suiteName: string;
    caseId: string;
    caseName: string;
    iteration: number;
    /** Set when a schedule ran it (on the server), not a person. */
    scheduleId?: string;
  };
}

/** Ms the customer waited for the run's reply — or, for a scenario, the mean. */
export const runResponseMs = (run: ValidateRun): number | null => {
  const replies = (
    run.focusIndex === null
      ? run.transcript
      : [run.transcript[run.focusIndex]].filter(Boolean)
  ).filter(
    (t): t is ValidateTurn & { latencyMs: number } =>
      t.role === 'bot' &&
      !t.seeded &&
      !t.system &&
      typeof t.latencyMs === 'number'
  );
  if (!replies.length) return null;
  return replies.reduce((sum, t) => sum + t.latencyMs, 0) / replies.length;
};

/**
 * The bot replies worth a turn score: the bot's own (not a platform message),
 * live (not seeded context), and answering something — a reply with a
 * customer message before it, so a greeting is not scored as an answer.
 */
export const scorableTurnIndices = (transcript: ValidateTurn[]): number[] => {
  const out: number[] = [];
  let sawUser = false;
  transcript.forEach((t, i) => {
    if (t.role === 'user') {
      sawUser = true;
      return;
    }
    if (sawUser && !t.seeded && !t.system) out.push(i);
  });
  return out;
};

export interface TurnTally {
  scored: number;
  passed: number;
  failed: number;
  pending: number;
}

/** How a run's turns scored so far. */
export const turnTally = (run: ValidateRun): TurnTally => {
  const scores = Object.values(run.turnScores ?? {});
  const scored = scores.filter(
    (sc): sc is Extract<RunScore, { status: 'scored' }> =>
      sc.status === 'scored'
  );
  const passed = scored.filter((sc) => passes(sc.verdict)).length;
  return {
    scored: scored.length,
    passed,
    failed: scored.length - passed,
    pending: scores.filter((sc) => sc.status === 'pending').length,
  };
};

export const runQuality = (run: ValidateRun): number | null =>
  run.score.status === 'scored' ? qualityScore(run.score.verdict) : null;

export const runPassed = (run: ValidateRun): boolean | null => {
  if (run.checks?.some((c) => !c.passed)) return false;
  return run.score.status === 'scored' ? passes(run.score.verdict) : null;
};

export type KindFilter = 'all' | 'test' | 'scenario' | 'failed';
export type TimeFilter = 'any' | 'hour' | 'today' | 'week';

export const TIME_FILTERS: { value: TimeFilter; label: string }[] = [
  { value: 'any', label: 'Any time' },
  { value: 'hour', label: 'Last hour' },
  { value: 'today', label: 'Today' },
  { value: 'week', label: 'Last 7 days' },
];

const sinceFor = (time: TimeFilter, now: number): number => {
  if (time === 'hour') return now - 60 * 60 * 1000;
  if (time === 'week') return now - 7 * 24 * 60 * 60 * 1000;
  if (time === 'today') {
    const start = new Date(now);
    start.setHours(0, 0, 0, 0);
    return start.getTime();
  }
  return -Infinity;
};

/** "Every version", or one published version's label, e.g. "v26". */
export type VersionFilter = 'any' | string;

const versionNumber = (label: string): number => {
  const m = /(\d+)\s*$/.exec(label);
  return m ? Number(m[1]) : -1;
};

/**
 * The versions the runs were made against, newest first, for the version
 * picker. A version's runs are what its scores are about, so picking one
 * starts the summary afresh after a publish without deleting anything.
 */
export const runVersions = (runs: ValidateRun[]): string[] =>
  Array.from(
    new Set(
      runs.map((r) => r.versionLabel?.trim()).filter((v): v is string => !!v)
    )
  ).sort((a, b) => versionNumber(b) - versionNumber(a) || b.localeCompare(a));

export const filterByVersion = (
  runs: ValidateRun[],
  version: VersionFilter
): ValidateRun[] =>
  version === 'any'
    ? runs
    : runs.filter((r) => r.versionLabel?.trim() === version);

/** One version's runs, summarised, for comparing versions side by side. */
export interface VersionSummary {
  version: string;
  runs: number;
  summary: RunSummary;
}

/** Each version's summary, newest version first. */
export const summarizeByVersion = (runs: ValidateRun[]): VersionSummary[] =>
  runVersions(runs).map((version) => {
    const mine = filterByVersion(runs, version);
    return { version, runs: mine.length, summary: summarizeRuns(mine) };
  });

export const filterByTime = (
  runs: ValidateRun[],
  time: TimeFilter,
  now: number
): ValidateRun[] => {
  const since = sinceFor(time, now);
  return runs.filter((r) => r.startedAt >= since);
};

export const filterByKind = (
  runs: ValidateRun[],
  kind: KindFilter
): ValidateRun[] => {
  if (kind === 'all') return runs;
  if (kind === 'failed') return runs.filter((r) => runPassed(r) === false);
  return runs.filter((r) => r.kind === kind);
};

export const kindCounts = (
  runs: ValidateRun[]
): Record<KindFilter, number> => ({
  all: runs.length,
  test: runs.filter((r) => r.kind === 'test').length,
  scenario: runs.filter((r) => r.kind === 'scenario').length,
  failed: runs.filter((r) => runPassed(r) === false).length,
});

export interface RunSummary {
  /** Runs that have a score — the ones every figure below is about. */
  scored: number;
  passed: number;
  failed: number;
  /** 0–100, rounded; null with nothing scored. */
  successRate: number | null;
  timed: number;
  avgResponseS: number | null;
  p95ResponseS: number | null;
  underTarget: number;
  qualityAvg: number | null;
  criterionAvgs: Record<CriterionKey, number> | null;
  /** The criteria those averages are over, in order: the rubric's, as scored. */
  criteriaSeen: VerdictCriterion[];
}

const percentile = (sorted: number[], p: number): number => {
  const i = Math.min(
    sorted.length - 1,
    Math.ceil((p / 100) * sorted.length) - 1
  );
  return sorted[Math.max(0, i)];
};

export const summarizeRuns = (runs: ValidateRun[]): RunSummary => {
  const scored = runs.filter(
    (r): r is ValidateRun & { score: { status: 'scored' } } =>
      r.score.status === 'scored'
  );
  const passed = scored.filter((r) => passes(r.score.verdict)).length;

  const times = runs
    .map(runResponseMs)
    .filter((ms): ms is number => ms !== null)
    .map((ms) => ms / 1000)
    .sort((a, b) => a - b);

  // Runs scored on different versions of the rubric each count toward the
  // criteria they were scored on.
  const criteriaSeen: VerdictCriterion[] = [];
  scored.forEach((r) =>
    verdictCriteria(r.score.verdict).forEach((c) => {
      if (!criteriaSeen.some((x) => x.key === c.key)) criteriaSeen.push(c);
    })
  );
  const criterionAvgs = scored.length
    ? (Object.fromEntries(
        criteriaSeen.map((c) => {
          const values = scored
            .map((r) => r.score.verdict.scores[c.key])
            .filter((v): v is number => typeof v === 'number');
          return [
            c.key,
            values.length
              ? values.reduce((a, b) => a + b, 0) / values.length
              : 0,
          ];
        })
      ) as Record<CriterionKey, number>)
    : null;

  return {
    scored: scored.length,
    passed,
    failed: scored.length - passed,
    successRate: scored.length
      ? Math.round((passed / scored.length) * 100)
      : null,
    timed: times.length,
    avgResponseS: times.length
      ? times.reduce((a, b) => a + b, 0) / times.length
      : null,
    p95ResponseS: times.length ? percentile(times, 95) : null,
    underTarget: times.filter((s) => s <= RESPONSE_TARGET_S).length,
    qualityAvg: scored.length
      ? Math.round(
          scored.reduce((sum, r) => sum + qualityScore(r.score.verdict), 0) /
            scored.length
        )
      : null,
    criterionAvgs,
    criteriaSeen,
  };
};

/** "2m ago", "3h ago", "Sep 24". */
export const relativeTime = (at: number, now: number): string => {
  const s = Math.max(0, Math.round((now - at) / 1000));
  if (s < 45) return 'just now';
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  return new Date(at).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
};

/** "2:05 PM" today, "Sep 28, 9:30 AM" before — when a run happened. */
export const absoluteTime = (at: number, now: number): string => {
  const d = new Date(at);
  const time = d.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  });
  if (d.toDateString() === new Date(now).toDateString()) return time;
  const sameYear = d.getFullYear() === new Date(now).getFullYear();
  const date = d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
  });
  return `${date}, ${time}`;
};

/* ---- Persistence --------------------------------------------------------- */

/** Oldest runs drop off past this, so one busy solution can't fill storage. */
export const MAX_RUNS = 100;

const storageKey = (solutionKey: string) =>
  `pro-studio:validate-runs:${solutionKey}`;

const isRun = (value: unknown): value is ValidateRun => {
  if (typeof value !== 'object' || value === null) return false;
  const r = value as Partial<ValidateRun>;
  return (
    typeof r.id === 'string' &&
    (r.kind === 'test' || r.kind === 'scenario') &&
    typeof r.title === 'string' &&
    typeof r.startedAt === 'number' &&
    Array.isArray(r.transcript) &&
    typeof r.score === 'object' &&
    r.score !== null
  );
};

/**
 * A run saved while its score was still coming never gets one: the request
 * that would have scored it died with the page. Say so rather than show a
 * spinner forever.
 */
const STALE: RunScore = {
  status: 'error',
  message: 'Scoring didn’t finish — the page was closed first.',
};

const settleStale = (run: ValidateRun): ValidateRun => ({
  ...run,
  score: run.score.status === 'pending' ? STALE : run.score,
  ...(run.turnScores
    ? {
        turnScores: Object.fromEntries(
          Object.entries(run.turnScores).map(([i, sc]) => [
            i,
            sc.status === 'pending' ? STALE : sc,
          ])
        ),
      }
    : {}),
});

/**
 * Runs saved when each Test reply was its own run, folded into one run per
 * conversation: the longest transcript that chat reached, each old run's
 * score kept as that reply's turn score, and the conversation itself not yet
 * scored. Scenario runs and already-merged runs pass through unchanged, and
 * the merged run keeps the newest old run's place in the list.
 */
export const mergeTestRunsByChat = (runs: ValidateRun[]): ValidateRun[] => {
  const legacy = new Map<string, ValidateRun[]>();
  runs.forEach((r) => {
    if (r.kind === 'test' && r.focusIndex !== null && r.chatId) {
      legacy.set(r.chatId, [...(legacy.get(r.chatId) ?? []), r]);
    }
  });
  if (!legacy.size) return runs;

  const merged = new Map<string, ValidateRun>();
  legacy.forEach((group, chatId) => {
    const longest = group.reduce((a, b) =>
      b.transcript.length > a.transcript.length ? b : a
    );
    const turnScores: Record<string, RunScore> = {};
    group.forEach((r) => {
      if (r.focusIndex !== null) turnScores[String(r.focusIndex)] = r.score;
    });
    merged.set(chatId, {
      ...longest,
      id: `chat-${chatId}`,
      title: latestUserText(longest.transcript) || longest.title,
      focusIndex: null,
      startedAt: Math.min(...group.map((r) => r.startedAt)),
      endedAt: Math.max(...group.map((r) => r.endedAt)),
      score: { status: 'idle' },
      turnScores,
    });
  });

  const placed = new Set<string>();
  return runs.flatMap((r) => {
    const group =
      r.kind === 'test' && r.focusIndex !== null ? legacy.get(r.chatId) : null;
    if (!group) return [r];
    if (placed.has(r.chatId)) return [];
    placed.add(r.chatId);
    return [merged.get(r.chatId) as ValidateRun];
  });
};

export const loadRuns = (
  solutionKey: string,
  storage: Pick<Storage, 'getItem'> | null = safeStorage()
): ValidateRun[] => {
  try {
    const raw = storage?.getItem(storageKey(solutionKey));
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed)
      ? mergeTestRunsByChat(parsed.filter(isRun).map(settleStale)).slice(
          0,
          MAX_RUNS
        )
      : [];
  } catch {
    return [];
  }
};

export const saveRuns = (
  solutionKey: string,
  runs: ValidateRun[],
  storage: Pick<Storage, 'setItem'> | null = safeStorage()
): void => {
  try {
    storage?.setItem(
      storageKey(solutionKey),
      JSON.stringify(runs.slice(0, MAX_RUNS))
    );
  } catch {
    // Full or blocked storage: the runs still show for this visit.
  }
};

function safeStorage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}
